import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/lib/LangContext";
import { caseStudies, caseOrder } from "@/lib/caseStudies";

function Case({ c, i }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1]);
  const isRight = i % 2 === 1;
  const slug = caseOrder.find((s) => caseStudies[s].logo === c.name);
  const externalUrl = slug ? caseStudies[slug].external_url : null;
  const slugKey = (slug || c.name).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

  return (
    <article
      ref={ref}
      className="grid grid-cols-1 lg:grid-cols-12 items-center gap-10 lg:gap-16 py-16 lg:py-24 border-t border-white/10"
      data-testid={`case-${slugKey}`}
    >
      <div className={`lg:col-span-7 ${isRight ? "lg:order-2" : ""}`}>
        <a
          href={externalUrl || "#"}
          target={externalUrl ? "_blank" : undefined}
          rel={externalUrl ? "noopener noreferrer" : undefined}
          className="block group"
        >
          <div className="relative overflow-hidden rounded-md clip-frame aspect-[4/3] lg:aspect-[16/10] bg-neutral-900">
            <motion.img
              style={{ y, scale }}
              src={c.image}
              alt={c.name}
              className="w-full h-full object-cover group-hover:brightness-110 transition-all"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-black/50 via-transparent to-black/30" />
            <div className="absolute bottom-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/50 backdrop-blur-md border border-white/15">
              <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
              <span className="text-[10px] uppercase tracking-[0.24em] text-white/80 font-mono-ef">
                {c.category}
              </span>
            </div>
          </div>
        </a>
      </div>

      <div className={`lg:col-span-5 ${isRight ? "lg:order-1" : ""}`}>
        <div className="font-mono-ef text-[10px] uppercase tracking-[0.24em] text-white/40 mb-4">
          Case · {String(i + 1).padStart(2, "0")}
        </div>
        <h3 className="font-display font-black text-5xl lg:text-6xl tracking-[-0.03em] leading-none">
          {c.name}
        </h3>
        <p className="mt-8 text-lg lg:text-xl text-white/75 leading-relaxed border-l-2 border-red-500 pl-5">
          "{c.quote}"
        </p>
        {externalUrl && (
          <a
            href={externalUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`case-cta-${slugKey}`}
            className="mt-8 inline-flex items-center gap-2 text-sm text-white/80 hover:text-red-400 transition-colors group"
          >
            <span className="border-b border-white/30 group-hover:border-red-400 pb-0.5">
              {externalUrl.replace(/^https?:\/\//, "").replace(/\/$/, "")}
            </span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M7 17L17 7M17 7H9M17 7v8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        )}
      </div>
    </article>
  );
}

export default function Trust() {
  const { t } = useLang();
  return (
    <section id="clients" className="relative py-28 lg:py-40" data-testid="trust-section">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-8 lg:mb-16">
          <div className="lg:col-span-6">
            <div className="text-[11px] uppercase tracking-[0.28em] text-red-400 font-mono-ef">
              — {t.trust.overline}
            </div>
            <h2 className="mt-6 font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-[-0.035em]">
              {t.trust.title}
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 lg:pt-4">
            <p className="text-lg text-white/60 leading-relaxed">{t.trust.subtitle}</p>
          </div>
        </div>

        <div className="mt-10">
          {t.trust.cases.map((c, i) => (
            <Case key={c.name} c={c} i={i} />
          ))}
        </div>

        <div className="mt-16 pt-10 border-t border-white/10 grid grid-cols-2 md:grid-cols-4 gap-6 items-center">
          <div className="font-display text-xl lg:text-2xl font-bold text-white/60 hover:text-white transition-colors text-center py-4">
            Donburi House
          </div>
          <div className="font-mono-ef text-[10px] uppercase tracking-[0.24em] text-white/35 text-center py-4">
            Riservato NDA
          </div>
          <div className="font-mono-ef text-[10px] uppercase tracking-[0.24em] text-white/35 text-center py-4">
            Riservato NDA
          </div>
          <div className="font-display text-xl lg:text-2xl font-bold text-white/40 text-center py-4">
            + 40 brand
          </div>
        </div>
      </div>
    </section>
  );
}
